/**
 * Helpers for keeping Supergrain documents prototype-free.
 *
 * Invariant: every document in the Supergrain store — and every nested object
 * inside one — is a null-prototype plain object (arrays keep `Array.prototype`).
 * It holds inductively across two enforcement points:
 *
 *   1. Inserts: every full-document insert funnels through the store's
 *      `prepareInsert` hook, which calls `stripPrototypesInPlace`
 *      (internal-supergrain-store.ts).
 *   2. Mutations: every patch value is cloned with `clonePrototypeFree` before
 *      mill applies it (supergrain-patch.ts), and the few direct writes to
 *      stored documents strip what they assign.
 *
 * Mill itself never imposes a prototype — undo snapshots and fabricated
 * branches preserve the document's flavor as of
 * https://github.com/commoncurriculum/supergrain/pull/123.
 */

/**
 * Recursively removes the prototype from `value` and every nested plain
 * object, in place. Arrays keep `Array.prototype` (their elements are still
 * visited) so array methods keep working.
 */
export function stripPrototypesInPlace(value: unknown, seen = new WeakSet<object>()): void {
  if (value === null || typeof value !== "object" || seen.has(value)) return
  seen.add(value)

  if (Array.isArray(value)) {
    for (const item of value) stripPrototypesInPlace(item, seen)
    return
  }

  Object.setPrototypeOf(value, null)
  for (const key of Object.keys(value)) {
    stripPrototypesInPlace((value as Record<string, unknown>)[key], seen)
  }
}

/**
 * Deep clone emitting null-prototype objects and plain arrays, preserving
 * shared references within one clone. Use instead of lodash `cloneDeep` (which
 * preserves the source's `Object.prototype`) when the copy is destined for a
 * Supergrain document.
 */
export function clonePrototypeFree<T>(value: T, seen = new WeakMap<object, unknown>()): T {
  if (value === null || typeof value !== "object") return value

  const already = seen.get(value)
  if (already !== undefined) return already as T

  if (value instanceof Date) return new Date(value) as T

  if (Array.isArray(value)) {
    const copy: unknown[] = []
    seen.set(value, copy)
    for (const item of value) copy.push(clonePrototypeFree(item, seen))
    return copy as T
  }

  const source = value as Record<string, unknown>
  const copy: Record<string, unknown> = Object.create(null)
  seen.set(value, copy)
  for (const key of Object.keys(source)) {
    copy[key] = clonePrototypeFree(source[key], seen)
  }
  return copy as T
}
